import $ from 'jquery';
import {disablePageScroll, enablePageScroll} from 'scroll-lock';

class Popup {

	constructor( popupEl ) {
		this.popupEl = popupEl;
		this.popupEl.classList.add( 'is-initialized' );

		// Set initial state.
		this.isOpen = popupEl.matches( 'dialog' ) ? popupEl.open : popupEl.classList.contains( 'en_is_active' );

		// Handle close correctly, if it wasn't called from us.
		this.popupEl.addEventListener( 'close', ( ev ) => {
			if ( this.isOpen ) {
				this.close( ev.target.returnValue );
			}
		} );
	}

	resolver = null;
	isOpen = false;

	open() {
		const popupEl = this.popupEl;

		// New Implementation: Trigger native JS Event.
		// Additionally: Make it cancelable with preventDefault().
		const doDefaultAction = popupEl.dispatchEvent( new CustomEvent( 'popup_open.sl', { bubbles: true, cancelable: true } ) );
		if ( ! doDefaultAction ) {
			return Promise.reject( 'default prevented' );
		}

		// Disable Page Scroll except inside the popup.
		const innerEl = popupEl.querySelector( '.mdl_popup__content' );
		disablePageScroll( innerEl );

		// Open the popup with animation.
		this.isOpen = true;
		popupEl.matches( 'dialog' ) ? popupEl.showModal() : popupEl.classList.add( 'en_is_active' );

		// Trigger native event afterwards.
		popupEl.dispatchEvent( new CustomEvent( 'popup_opened.sl', { bubbles: true } ) );

		// Old jQuery Event - Keep for legacy reasons.
		$( popupEl ).trigger( 'popup_open.en' );

		// Add promise to resolve on popupclose.
		if ( ! this.resolver ) {
			if ( 'function' === typeof Promise.withResolvers ) {
				this.resolver = Promise.withResolvers();
			} else {
				const resolver = {};
				resolver.promise = new Promise( ( resolveFn, rejectFn ) => {
					resolver.resolve = resolveFn;
					resolver.reject = rejectFn;
				} );
				this.resolver = resolver;
			}

		}
		return this.resolver.promise;
	}

	close( returnValue = '' ) {
		const popupEl = this.popupEl;

		// New Implementation: Trigger native JS Event.
		// Additionally: Make it cancelable with preventDefault().
		const doDefaultAction = popupEl.dispatchEvent( new CustomEvent( 'popup_close.sl', { bubbles: true, cancelable: true, detail: { returnValue } } ) );
		if ( ! doDefaultAction ) {
			return false;
		}

		// Re-enable Page Scroll except inside the popup.
		const innerEl = popupEl.querySelector( '.mdl_popup__content' );
		enablePageScroll( innerEl );

		// Close the popup with animation.
		this.isOpen = false;
		popupEl.matches( 'dialog' ) && popupEl.close( returnValue );
		popupEl.classList.remove( 'en_is_active' );

		// Trigger native event afterwards.
		popupEl.dispatchEvent( new CustomEvent( 'popup_closed.sl', { bubbles: true } ) );

		// Old jQuery Event - Keep for legacy reasons.
		$( popupEl ).trigger( 'popup_close.en' );

		// Resolve promise we returned in open function.
		if ( this.resolver ) {
			this.resolver.resolve( returnValue );
			this.resolver = null;
		}
		return returnValue;
	}

	static getPopupElement( targetSel ) {
		if ( targetSel instanceof HTMLElement ) {
			return targetSel;
		} else if ( targetSel instanceof $ ) {
			return targetSel.get( 0 );
		} else {
			return document.querySelector( targetSel );
		}
	}

	static getPopup( targetSel ) {
		const popupEl = Popup.getPopupElement( targetSel );

		// Invalid Popup Element.
		if ( ! popupEl ) {
			return null;
		}

		// Create Popup Instance, if not exist yet.
		popupEl.KOE_POPUP ??= new Popup( popupEl );
		return popupEl.KOE_POPUP;
	}


	loadContent( contentLink ) {
		this.popupEl.classList.add( 'en_is_loading' );
		return Promise.resolve().then( () => {
			return fetch( contentLink.toString() ).then( async( res ) => {
				if ( res.ok ) {
					if ( 'application/pdf' === res.headers.get( 'Content-Type' ) ) {
						return await res.blob();
					} else {
						return await res.text();
					}
				}

				return Promise.reject( await res.text() );
			} ).then( ( response ) => {
				if ( response instanceof Blob ) {
					return this.openBlobInIFrame( response );
				}

				const fragment = document.createRange().createContextualFragment( response );
				const contentEls = fragment.querySelector( '.mdl_block_acf-classic-editor .en_wysiwyg' );

				this.popupEl.querySelector( '.mdl_popup__body' ).replaceChildren( ...contentEls );
			} )
		} ).catch( ( error ) => {
			console.error( error ); // eslint-disable-line no-console

			// Fallback, in case something went wrong.
			window.open( contentLink.toString(), '_blank' );
			this.close();
		} ).finally( () => {
			this.popupEl.classList.remove( 'en_is_loading' );
		} );
	}

	openBlobInIFrame( source ) {
		const dataUrl = source instanceof Blob ? URL.createObjectURL( source ) : source;
		return new Promise( ( resolve, reject ) => {
			const frameEl = document.createElement( 'iframe' );
			frameEl.className = 'mdl_popup__body_iframe';
			frameEl.addEventListener( 'load', () => resolve() );
			frameEl.addEventListener( 'error', () => reject() );
			frameEl.src = dataUrl.toString();

			this.popupEl.querySelector( '.mdl_popup__body' ).replaceChildren( frameEl );
		} ).finally( () => {
			if ( source instanceof Blob ) {
				setTimeout( () => URL.revokeObjectURL( dataUrl ), 10000 );
			}
		} );
	}
}

const initPopupBindings = function() {

	// Handle click event on any element, no matter when its clicked.
	document.addEventListener( 'click', ( ev ) => {
		if ( ev.target.matches( '.mdl_popup' ) ) {
			ev.preventDefault();
			close_popup();
		} else if ( ev.target.closest( '.en_js_close_popup' ) ) {
			ev.preventDefault();
			close_popup();

		} else if ( ev.target.closest( '.en_js_open_popup' ) ) {
			ev.preventDefault();

			const triggerEl = ev.target.closest( '.en_js_open_popup' );
			const targetSel = triggerEl.getAttribute( 'data-en-popup-target' ) || triggerEl.getAttribute( 'href' );
			open_popup( targetSel );

			if ( triggerEl.classList.contains( 'en_js_load_popup' ) ) {
				Popup.getPopup( targetSel )?.loadContent( triggerEl.href );
			}
		}
	} );

	// Close Popup when the ESC Key is pressed.
	document.addEventListener( 'keydown', ( ev ) => {
		if ( 27 === ev.keyCode || 'Escape' === ev.key ) {
			if ( document.querySelector( '.mdl_popup[open],.mdl_popup.en_is_active' ) ) {
				ev.preventDefault();
				close_popup();
			}
		}
	} );

	// Additional state class to document element.
	document.addEventListener( 'popup_opened.sl', () => {
		// Add popup_active class to document.
		document.documentElement.classList.add( 'popup_active' );
	} );
	document.addEventListener( 'popup_closed.sl', () => {
		// Remove popup_active class from document.
		document.documentElement.classList.remove( 'popup_active' );
	} );

	// Close all other open Popups, before we open this one.
	window.addEventListener( 'popup_open.sl', ( ev ) => {
		if ( ! ev.defaultPrevented ) {
			// Check all popups on the page.
			const allPopupEls = document.querySelectorAll( '.mdl_popup' );
			allPopupEls.forEach( ( otherEl ) => {
				if ( otherEl !== ev.target ) {
					const OtherPopup = Popup.getPopup( otherEl );
					if ( OtherPopup && OtherPopup.isOpen ) {
						OtherPopup.close();
					}
				}
			} );
		}
	} );
};

/**
 * Open the given Popup.
 *
 * @param targetSel Either HTMLElement or querySelector string.
 */
const open_popup = function( targetSel ) {
	return Popup.getPopup( targetSel )?.open();
};

/**
 * Close all currently open popups.
 * @returns {*}
 */
const close_popup = function( targetSel = undefined, returnValue = '' ) {

	// Get all active popups.
	const popupEls = Array.from( 'undefined' !== typeof targetSel ? [ Popup.getPopupElement( targetSel ) ] : document.querySelectorAll( '.mdl_popup[open],.mdl_popup.en_is_active' ) );
	popupEls.forEach( popupEl => {
		// Some popups can not be closed this way.
		const closeable = popupEl.dataset.closeable;
		if ( closeable !== 'false' && closeable !== false ) {
			Popup.getPopup( popupEl )?.close( returnValue );
		}
	} );

	return popupEls;
};

const notifyIfAvailable = function() {
	// Open popup if certain hash is given
	if ( location.hash.indexOf( '#benachrichtigen' ) !== -1 ) {

		if ( document.getElementById( 'popup_notifyifavailableagain' ) ) {
			open_popup( '#popup_notifyifavailableagain' );
		} else {
			open_popup( '#popup_notifyifavailable' );
		}

		history.pushState( "", document.title, location.pathname + location.search );
	}
};

const outdatedBrowser = function() {
	// Check for ie
	if ( document.documentMode ) {
		open_popup( '#popup_outdated_browser' );
	}
}

export {
	Popup as default,
	open_popup,
	close_popup,
	initPopupBindings,
	notifyIfAvailable,
	outdatedBrowser
};
